import { useCallback, useEffect, useRef } from "react";
import type { AnalyticsSettings } from "@/hooks/useAnalyticsSettings";

/**
 * Latest video playback position for resume-on-reopen. Held in a ref and
 * flushed to settings only when playback is being left (pause, history
 * traversal, unmount): persisting on every timeupdate re-rendered the whole
 * page several times a second, and that continuous stream of urgent updates
 * could indefinitely starve React Router's transition-based route swap —
 * browser back changed the URL while the analytics screen stayed on display
 * (issue #1151).
 */
export function useVideoResumePosition(
  initialTime: number,
  persistPatch: (patch: Partial<AnalyticsSettings>) => void,
): {
  recordTime: (time: number) => void;
  flush: () => void;
} {
  const videoCurrentTimeRef = useRef(initialTime);

  const flush = useCallback(() => {
    persistPatch({ videoCurrentTime: videoCurrentTimeRef.current });
  }, [persistPatch]);

  // Flush the pending position whenever the page is being left. popstate and
  // pagehide cannot rely on a state-update-driven save effect (the router's
  // transition may be starved, and the component will not re-render after
  // pagehide), so the flush writes storage synchronously via persistPatch.
  useEffect(() => {
    const handlePageLeave = () => {
      flush();
    };
    window.addEventListener("popstate", handlePageLeave);
    window.addEventListener("pagehide", handlePageLeave);
    return () => {
      window.removeEventListener("popstate", handlePageLeave);
      window.removeEventListener("pagehide", handlePageLeave);
      flush();
    };
  }, [flush]);

  const recordTime = useCallback((time: number) => {
    videoCurrentTimeRef.current = time;
  }, []);

  return { recordTime, flush };
}
